import React from "react";
import { useNavigate } from "react-router-dom";
import { Globe, ListChecks, Mail, FileText, Calendar, Euro, Hash, User as UserIcon, FileType, AlertTriangle, Share2, MessageCircle, CalendarPlus } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/state/AppContext";

const URGENCY = {
  green: { label: "Keine Aktion notwendig", color: "bg-emerald-50 border-emerald-200 text-emerald-700", dot: "bg-emerald-500", glow: "tl-glow-green", icon: "🟢" },
  yellow: { label: "Aktion erforderlich", color: "bg-amber-50 border-amber-200 text-amber-700", dot: "bg-amber-500", glow: "tl-glow-yellow", icon: "🟡" },
  red: { label: "Dringend", color: "bg-red-50 border-red-200 text-red-700", dot: "bg-red-500", glow: "tl-glow-red", icon: "🔴" },
};

function formatDate(d) {
  if (!d) return null;
  try {
    const dt = new Date(d);
    if (isNaN(dt)) return d;
    return dt.toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" });
  } catch { return d; }
}

function formatAmount(a, cur = "EUR") {
  if (a === null || a === undefined) return null;
  try {
    return new Intl.NumberFormat("de-DE", { style: "currency", currency: cur || "EUR" }).format(a);
  } catch { return `${a} ${cur || ""}`; }
}

export default function ResultPage() {
  const { currentResult } = useApp();
  const navigate = useNavigate();

  if (!currentResult) {
    return (
      <div className="p-6" data-testid="result-empty">
        <p className="text-slate-600">Kein Ergebnis vorhanden.</p>
        <button onClick={() => navigate("/upload")} className="mt-4 px-5 py-3 rounded-2xl bg-blue-600 text-white font-semibold" data-testid="empty-goto-upload">
          Brief hochladen
        </button>
      </div>
    );
  }

  const a = currentResult.analysis || {};
  const urgency = URGENCY[a.urgency] || URGENCY.green;
  const plain = currentResult.plain_explanation || a.summary;

  const cards = [
    a.deadline && { icon: <Calendar className="w-5 h-5 text-blue-600" />, label: "Frist", value: formatDate(a.deadline) },
    (a.amount !== null && a.amount !== undefined) && { icon: <Euro className="w-5 h-5 text-blue-600" />, label: "Betrag", value: formatAmount(a.amount, a.currency) },
    a.reference_number && { icon: <Hash className="w-5 h-5 text-blue-600" />, label: "Aktenzeichen", value: a.reference_number },
    a.sender && { icon: <UserIcon className="w-5 h-5 text-blue-600" />, label: "Absender", value: a.sender },
    a.document_type && { icon: <FileType className="w-5 h-5 text-blue-600" />, label: "Dokumenttyp", value: a.document_type },
    a.document_date && { icon: <Calendar className="w-5 h-5 text-blue-600" />, label: "Dokumentdatum", value: formatDate(a.document_date) },
  ].filter(Boolean);

  return (
    <div className="px-5 pt-6 pb-10 space-y-5" data-testid="result-page">
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Absender</div>
        <h1 className="text-2xl font-bold text-slate-900" data-testid="result-sender">{a.sender || "Unbekannt"}</h1>
        {a.document_type && (
          <div className="mt-1 inline-block px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-sm font-medium" data-testid="result-doctype">
            {a.document_type}
          </div>
        )}
      </div>

      {/* Traffic light banner */}
      <div className={`rounded-3xl border p-4 ${urgency.color}`} data-testid={`urgency-${a.urgency || "green"}`}>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <span className={`w-3 h-3 rounded-full ${a.urgency === "red" ? "bg-red-500 " + urgency.glow : "bg-red-200"}`} />
            <span className={`w-3 h-3 rounded-full ${a.urgency === "yellow" ? "bg-amber-500 " + urgency.glow : "bg-amber-200"}`} />
            <span className={`w-3 h-3 rounded-full ${a.urgency === "green" ? "bg-emerald-500 " + urgency.glow : "bg-emerald-200"}`} />
          </div>
          <div className="font-semibold">{urgency.icon} {urgency.label}</div>
        </div>
        {plain && <p className="mt-3 text-[15px] leading-relaxed text-slate-800">{plain}</p>}
      </div>

      {currentResult.warning_legal && (
        <div className="rounded-2xl bg-red-50 border border-red-200 p-4 flex items-start gap-3" data-testid="legal-warning">
          <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
          <p className="text-sm text-red-900 leading-relaxed">
            Dieses Schreiben kann rechtliche oder finanzielle Folgen haben. BriefAssist dient zur Orientierung und ersetzt keine professionelle Rechts-, Steuer- oder Finanzberatung.
          </p>
        </div>
      )}

      {cards.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-slate-900 mb-3">Wichtige Informationen</h2>
          <div className="grid grid-cols-2 gap-3">
            {cards.map((c, i) => (
              <div key={i} className="rounded-2xl bg-blue-50/60 border border-blue-100 p-3" data-testid={`info-card-${i}`}>
                <div className="flex items-center gap-2 mb-1">{c.icon}<span className="text-xs text-slate-500 font-medium">{c.label}</span></div>
                <div className="text-[15px] font-semibold text-slate-900 leading-tight">{c.value}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {a.required_action && (
        <div className="rounded-3xl bg-white border border-slate-200 p-4 shadow-sm">
          <h3 className="text-base font-semibold text-slate-900">Was musst du tun?</h3>
          <p className="mt-2 text-slate-700 text-[15px] leading-relaxed">{a.required_action}</p>
        </div>
      )}

      {a.consequences && (
        <div className="rounded-3xl bg-white border border-slate-200 p-4 shadow-sm">
          <h3 className="text-base font-semibold text-slate-900">Was passiert, wenn du nichts tust?</h3>
          <p className="mt-2 text-slate-700 text-[15px] leading-relaxed">{a.consequences}</p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <button onClick={() => navigate("/translation")} data-testid="action-translate-btn"
          className="min-h-[54px] rounded-2xl bg-blue-600 text-white font-semibold flex items-center justify-center gap-2 active:scale-[0.98] transition">
          <Globe className="w-5 h-5" /> In meiner Sprache
        </button>
        <button onClick={() => navigate("/actions")} data-testid="action-todo-btn"
          className="min-h-[54px] rounded-2xl bg-blue-50 text-blue-700 font-semibold flex items-center justify-center gap-2 border border-blue-100 active:scale-[0.98] transition">
          <ListChecks className="w-5 h-5" /> Was soll ich tun?
        </button>
        <button onClick={() => navigate("/reply")} data-testid="action-reply-btn"
          className="min-h-[54px] rounded-2xl bg-blue-50 text-blue-700 font-semibold flex items-center justify-center gap-2 border border-blue-100 active:scale-[0.98] transition">
          <Mail className="w-5 h-5" /> Antwort erstellen
        </button>
        <button onClick={() => navigate("/translation?tab=full")} data-testid="action-original-btn"
          className="min-h-[54px] rounded-2xl bg-white text-slate-700 font-semibold flex items-center justify-center gap-2 border border-slate-200 active:scale-[0.98] transition">
          <FileText className="w-5 h-5" /> Original
        </button>
      </div>

      {typeof a.confidence === "number" && a.confidence < 0.6 && (
        <div className="rounded-2xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800" data-testid="low-confidence">
          ⚠️ Einige Informationen konnten nicht sicher erkannt werden. Möglicherweise fehlt eine Seite oder das Foto ist unscharf.
        </div>
      )}

      <ShareBlock analysis={a} plain={plain} />
      {a.deadline && <CalendarBlock analysis={a} />}
    </div>
  );
}

function pad(n) { return String(n).padStart(2, "0"); }
function toCalendarUTC(dateStr) {
  const d = new Date(dateStr);
  if (isNaN(d)) return null;
  // Full-day event UTC (YYYYMMDD)
  const y = d.getUTCFullYear(); const m = pad(d.getUTCMonth() + 1); const day = pad(d.getUTCDate());
  const next = new Date(d.getTime() + 24 * 60 * 60 * 1000);
  const y2 = next.getUTCFullYear(); const m2 = pad(next.getUTCMonth() + 1); const d2 = pad(next.getUTCDate());
  return { start: `${y}${m}${day}`, end: `${y2}${m2}${d2}` };
}

function CalendarBlock({ analysis }) {
  const dates = toCalendarUTC(analysis.deadline);
  if (!dates) return null;
  const title = `Frist: ${analysis.sender || "Brief"}`;
  const details = `${analysis.required_action || analysis.summary || ""}\n\nGeneriert von BriefAssist.`;
  const gcalUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE`
    + `&text=${encodeURIComponent(title)}`
    + `&dates=${dates.start}/${dates.end}`
    + `&details=${encodeURIComponent(details)}`;

  const downloadICS = () => {
    const uid = `briefassist-${Date.now()}@briefassist`;
    const dtStamp = new Date().toISOString().replace(/[-:]/g, "").replace(/\.\d+/, "");
    const ics = [
      "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//BriefAssist//DE",
      "CALSCALE:GREGORIAN", "METHOD:PUBLISH",
      "BEGIN:VEVENT",
      `UID:${uid}`, `DTSTAMP:${dtStamp}`,
      `DTSTART;VALUE=DATE:${dates.start}`,
      `DTEND;VALUE=DATE:${dates.end}`,
      `SUMMARY:${title.replace(/\n/g, " ")}`,
      `DESCRIPTION:${details.replace(/\n/g, "\\n")}`,
      "END:VEVENT", "END:VCALENDAR"
    ].join("\r\n");
    const blob = new Blob([ics], { type: "text/calendar" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url; link.download = `briefassist-frist-${dates.start}.ics`;
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 500);
  };

  return (
    <div className="rounded-3xl bg-white border border-slate-200 p-4 shadow-sm space-y-3" data-testid="calendar-block">
      <div className="flex items-center gap-2 text-slate-900 font-semibold">
        <CalendarPlus className="w-5 h-5 text-blue-600" /> Frist im Kalender speichern
      </div>
      <div className="grid grid-cols-2 gap-3">
        <a href={gcalUrl} target="_blank" rel="noopener noreferrer" data-testid="cal-google-btn"
          className="min-h-[52px] rounded-2xl bg-blue-600 text-white font-semibold flex items-center justify-center gap-2 active:scale-[0.98]">
          Google Kalender
        </a>
        <button onClick={downloadICS} data-testid="cal-ics-btn"
          className="min-h-[52px] rounded-2xl bg-blue-50 text-blue-700 font-semibold flex items-center justify-center gap-2 border border-blue-100 active:scale-[0.98]">
          Apple / Outlook (.ics)
        </button>
      </div>
    </div>
  );
}

function ShareBlock({ analysis, plain }) {
  const shareText = `📬 ${analysis.sender || "Brief"}${analysis.document_type ? " – " + analysis.document_type : ""}\n\n${plain || analysis.summary || ""}\n\n– via BriefAssist`;
  const doShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: "BriefAssist", text: shareText });
        return;
      }
      await navigator.clipboard.writeText(shareText);
      toast.success("Zusammenfassung kopiert.");
    } catch (_) { void 0; }
  };
  const waUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
  return (
    <div className="grid grid-cols-2 gap-3">
      <button onClick={doShare} data-testid="share-btn"
        className="min-h-[52px] rounded-2xl bg-white text-slate-700 font-semibold flex items-center justify-center gap-2 border border-slate-200 active:scale-[0.98]">
        <Share2 className="w-5 h-5" /> Teilen
      </button>
      <a href={waUrl} target="_blank" rel="noopener noreferrer" data-testid="share-wa-btn"
        className="min-h-[52px] rounded-2xl bg-emerald-50 text-emerald-700 font-semibold flex items-center justify-center gap-2 border border-emerald-100">
        <MessageCircle className="w-5 h-5" /> WhatsApp
      </a>
    </div>
  );
}
